import { supabase } from "./client";
import { logger } from "@/utils/logger";

/**
 * Interface para categorias de produto
 */
export interface ProductCategory {
  id: string;
  name: string;
  empresa_id: string;
  is_predefined: boolean;  // true = pré-definida para todos, false = customizada
  created_at: string;
  created_by: string | null;
}

/**
 * Interface para tipos de pedido/orçamento
 */
export interface OrderType {
  id: string;
  name: string;
  empresa_id: string;
  is_predefined: boolean;  // true = pré-definida para todos, false = customizada
  created_at: string;
  created_by: string | null;
}

/**
 * Interface para tipos de tecido
 */
export interface FabricType {
  id: string;
  name: string;
  empresa_id: string;
  is_predefined: boolean;  // true = pré-definido para todos, false = customizado
  created_at: string;
  created_by: string | null;
}

/**
 * Interface para tipos de cortina
 */
export interface CurtainType {
  id: string;
  name: string;
  empresa_id: string;
  is_predefined: boolean;  // true = pré-definido para todos, false = customizado
  created_at: string;
  created_by: string | null;
}

/**
 * Buscar todas as categorias da empresa
 */
export async function getProductCategories(empresaId: string): Promise<ProductCategory[]> {
  try {
    logger.debug(`📦 Buscando categorias de produtos para empresa ${empresaId}`);
    
    const { data, error } = await supabase
      .from("product_categories")
      .select("*")
      .eq("empresa_id", empresaId)
      .order("name", { ascending: true });

    if (error) {
      logger.error(`❌ Erro ao buscar categorias: ${error.message}`);
      throw error;
    }

    logger.debug(`✅ ${data?.length || 0} categorias encontradas`);
    return data || [];
  } catch (error) {
    logger.error("❌ Erro ao buscar categorias de produtos:", error);
    throw error;
  }
}

/**
 * Criar uma nova categoria de produto (customizada pelo usuário)
 */
export async function createProductCategory(
  empresaId: string,
  categoryName: string,
  userId: string
): Promise<ProductCategory> {
  try {
    logger.debug(`➕ Criando categoria customizada: ${categoryName}`);
    
    const { data, error } = await supabase
      .from("product_categories")
      .insert({
        name: categoryName.trim(),
        empresa_id: empresaId,
        created_by: userId,
        is_predefined: false,  // Categorias criadas por usuários NÃO são pré-definidas
      })
      .select()
      .single();

    if (error) {
      logger.error(`❌ Erro ao criar categoria: ${error.message}`);
      throw error;
    }

    logger.debug(`✅ Categoria customizada criada com sucesso: ${data.id}`);
    return data;
  } catch (error) {
    logger.error("❌ Erro ao criar categoria de produto:", error);
    throw error;
  }
}

/**
 * Deletar uma categoria de produto
 */
export async function deleteProductCategory(categoryId: string): Promise<void> {
  try {
    logger.debug(`🗑️ Deletando categoria: ${categoryId}`);
    
    const { error } = await supabase
      .from("product_categories")
      .delete()
      .eq("id", categoryId);

    if (error) {
      logger.error(`❌ Erro ao deletar categoria: ${error.message}`);
      throw error;
    }

    logger.debug(`✅ Categoria deletada com sucesso`);
  } catch (error) {
    logger.error("❌ Erro ao deletar categoria de produto:", error);
    throw error;
  }
}

/**
 * Buscar todos os tipos de pedido da empresa
 */
export async function getOrderTypes(empresaId: string): Promise<OrderType[]> {
  try {
    logger.debug(`📋 Buscando tipos de pedido para empresa ${empresaId}`);
    
    const { data, error } = await supabase
      .from("order_types")
      .select("*")
      .eq("empresa_id", empresaId)
      .order("name", { ascending: true });

    if (error) {
      logger.error(`❌ Erro ao buscar tipos de pedido: ${error.message}`);
      throw error;
    }

    logger.debug(`✅ ${data?.length || 0} tipos de pedido encontrados`);
    return data || [];
  } catch (error) {
    logger.error("❌ Erro ao buscar tipos de pedido:", error);
    throw error;
  }
}

/**
 * Criar um novo tipo de pedido (customizado pelo usuário)
 */
export async function createOrderType(
  empresaId: string,
  typeName: string,
  userId: string
): Promise<OrderType> {
  try {
    logger.debug(`➕ Criando tipo de pedido customizado: ${typeName}`);
    
    const { data, error } = await supabase
      .from("order_types")
      .insert({
        name: typeName.trim(),
        empresa_id: empresaId,
        created_by: userId,
        is_predefined: false,  // Tipos criados por usuários NÃO são pré-definidos
      })
      .select()
      .single();

    if (error) {
      logger.error(`❌ Erro ao criar tipo de pedido: ${error.message}`);
      throw error;
    }

    logger.debug(`✅ Tipo de pedido customizado criado com sucesso: ${data.id}`);
    return data;
  } catch (error) {
    logger.error("❌ Erro ao criar tipo de pedido:", error);
    throw error;
  }
}

/**
 * Deletar um tipo de pedido
 */
export async function deleteOrderType(typeId: string): Promise<void> {
  try {
    logger.debug(`🗑️ Deletando tipo de pedido: ${typeId}`);

    const { error } = await supabase
      .from("order_types")
      .delete()
      .eq("id", typeId);

    if (error) {
      logger.error(`❌ Erro ao deletar tipo de pedido: ${error.message}`);
      throw error;
    }

    logger.debug(`✅ Tipo de pedido deletado com sucesso`);
  } catch (error) {
    logger.error("❌ Erro ao deletar tipo de pedido:", error);
    throw error;
  }
}

/**
 * Buscar todos os tipos de tecido da empresa
 */
export async function getFabricTypes(empresaId: string): Promise<FabricType[]> {
  try {
    logger.debug(`🧵 Buscando tipos de tecido para empresa ${empresaId}`);

    const { data, error } = await supabase
      .from("fabric_types")
      .select("*")
      .eq("empresa_id", empresaId)
      .order("name", { ascending: true });

    if (error) {
      logger.error(`❌ Erro ao buscar tipos de tecido: ${error.message}`);
      throw error;
    }

    logger.debug(`✅ ${data?.length || 0} tipos de tecido encontrados`);
    return data || [];
  } catch (error) {
    logger.error("❌ Erro ao buscar tipos de tecido:", error);
    throw error;
  }
}

/**
 * Criar um novo tipo de tecido (customizado pelo usuário)
 */
export async function createFabricType(
  empresaId: string,
  fabricName: string,
  userId: string
): Promise<FabricType> {
  try {
    logger.debug(`➕ Criando tipo de tecido customizado: ${fabricName}`);

    const { data, error } = await supabase
      .from("fabric_types")
      .insert({
        name: fabricName.trim(),
        empresa_id: empresaId,
        created_by: userId,
        is_predefined: false,  // Tipos criados por usuários NÃO são pré-definidos
      })
      .select()
      .single();

    if (error) {
      logger.error(`❌ Erro ao criar tipo de tecido: ${error.message}`);
      throw error;
    }

    logger.debug(`✅ Tipo de tecido customizado criado com sucesso: ${data.id}`);
    return data;
  } catch (error) {
    logger.error("❌ Erro ao criar tipo de tecido:", error);
    throw error;
  }
}

/**
 * Deletar um tipo de tecido
 */
export async function deleteFabricType(fabricTypeId: string): Promise<void> {
  try {
    logger.debug(`🗑️ Deletando tipo de tecido: ${fabricTypeId}`);

    const { error } = await supabase
      .from("fabric_types")
      .delete()
      .eq("id", fabricTypeId);

    if (error) {
      logger.error(`❌ Erro ao deletar tipo de tecido: ${error.message}`);
      throw error;
    }

    logger.debug(`✅ Tipo de tecido deletado com sucesso`);
  } catch (error) {
    logger.error("❌ Erro ao deletar tipo de tecido:", error);
    throw error;
  }
}

/**
 * Buscar todos os tipos de cortina da empresa
 */
export async function getCurtainTypes(empresaId: string): Promise<CurtainType[]> {
  try {
    logger.debug(`🪟 Buscando tipos de cortina para empresa ${empresaId}`);

    const { data, error } = await supabase
      .from("curtain_types")
      .select("*")
      .or(`empresa_id.eq.${empresaId},empresa_id.is.null`)
      .order("name", { ascending: true });

    if (error) {
      logger.error(`❌ Erro ao buscar tipos de cortina: ${error.message}`);
      throw error;
    }

    logger.debug(`✅ ${data?.length || 0} tipos de cortina encontrados`);
    return data || [];
  } catch (error) {
    logger.error("❌ Erro ao buscar tipos de cortina:", error);
    throw error;
  }
}

/**
 * Criar um novo tipo de cortina (customizado pelo usuário)
 */
export async function createCurtainType(
  empresaId: string,
  curtainName: string,
  userId: string
): Promise<CurtainType> {
  try {
    logger.debug(`➕ Criando tipo de cortina customizado: ${curtainName}`);

    const { data, error } = await supabase
      .from("curtain_types")
      .insert({
        name: curtainName.trim(),
        empresa_id: empresaId,
        created_by: userId,
        is_predefined: false,  // Tipos criados por usuários NÃO são pré-definidos
      })
      .select()
      .single();

    if (error) {
      logger.error(`❌ Erro ao criar tipo de cortina: ${error.message}`);
      throw error;
    }

    logger.debug(`✅ Tipo de cortina customizado criado com sucesso: ${data.id}`);
    return data;
  } catch (error) {
    logger.error("❌ Erro ao criar tipo de cortina:", error);
    throw error;
  }
}

/**
 * Deletar um tipo de cortina
 */
export async function deleteCurtainType(curtainTypeId: string): Promise<void> {
  try {
    logger.debug(`🗑️ Deletando tipo de cortina: ${curtainTypeId}`);

    const { error } = await supabase
      .from("curtain_types")
      .delete()
      .eq("id", curtainTypeId);

    if (error) {
      logger.error(`❌ Erro ao deletar tipo de cortina: ${error.message}`);
      throw error;
    }

    logger.debug(`✅ Tipo de cortina deletado com sucesso`);
  } catch (error) {
    logger.error("❌ Erro ao deletar tipo de cortina:", error);
    throw error;
  }
}
